import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as CapitalizeIcon,
  SwapHoriz as TransferIcon,
  Sell as DisposeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, BranchPicker, CostCentrePicker, DepartmentPicker, LocationPicker, SupplierPicker } from '../LedgerPickers';
import { AssetCategoryPicker, AssetStatusChip, assetIdOf, fmtDate, getErrorMessage, unwrap } from './AssetsShared';

const STATUSES = ['DRAFT', 'APPROVED', 'ACTIVE', 'DISPOSED', 'RETIRED'];
const DISPOSAL_TYPES = ['SALE', 'SCRAP', 'RETIREMENT'].map((t) => ({ label: t, value: t }));

const emptyForm = () => ({
  asset_name: '',
  category_id: '',
  parent_id: '',
  acquisition_date: new Date().toISOString().slice(0, 10),
  cost_amount: '',
  salvage_value: '',
  supplier_id: '',
  department_id: '',
  location_id: '',
  cost_centre_id: '',
  branch_id: '',
  custodian: '',
  description: '',
});

// Fixed asset register (verified against AssetController/AssetService):
// DRAFT -> APPROVED (approve) -> ACTIVE (capitalise with a funding leg).
// Transfer moves custody between departments/locations; disposal retires the
// asset (DISPOSED, or RETIRED for retirements) and posts the gain/loss.
const AssetRegisterTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.ASSETS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.ASSETS, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.ASSETS, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.ASSETS, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [capDlg, setCapDlg] = useState({ open: false, item: null, funding_account_id: '', capitalization_date: new Date().toISOString().slice(0, 10), supplier_id: '' });
  const [transferDlg, setTransferDlg] = useState({ open: false, item: null, department_id: '', location_id: '', cost_centre_id: '', branch_id: '', custodian: '', reason: '' });
  const [disposeDlg, setDisposeDlg] = useState({ open: false, item: null, disposal_type: 'SALE', disposal_date: new Date().toISOString().slice(0, 10), proceeds: '', settlement_account_id: '', gain_loss_account_id: '', notes: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.asset_name.trim()) {
      showSnackbar('Asset name is required', 'error');
      return;
    }
    if (!values.category_id) {
      showSnackbar('Category is required', 'error');
      return;
    }
    if (!(Number(values.cost_amount) > 0)) {
      showSnackbar('Cost amount must be greater than zero', 'error');
      return;
    }
    const num = (v) => (v !== '' && v != null ? Number(v) : undefined);
    const payload = {
      asset_name: values.asset_name.trim(),
      ...(values.description?.trim() ? { description: values.description.trim() } : {}),
      category_id: Number(values.category_id),
      ...(values.parent_id ? { parent_id: Number(values.parent_id) } : {}),
      acquisition_date: values.acquisition_date,
      cost_amount: Number(values.cost_amount),
      ...(num(values.salvage_value) !== undefined ? { salvage_value: num(values.salvage_value) } : {}),
      ...(values.supplier_id ? { supplier_id: Number(values.supplier_id) } : {}),
      ...(values.department_id ? { department_id: Number(values.department_id) } : {}),
      ...(values.location_id ? { location_id: Number(values.location_id) } : {}),
      ...(values.cost_centre_id ? { cost_centre_id: Number(values.cost_centre_id) } : {}),
      ...(values.branch_id ? { branch_id: Number(values.branch_id) } : {}),
      ...(values.custodian?.trim() ? { custodian: values.custodian.trim() } : {}),
    };
    try {
      setSaving(true);
      await ledgerAPI.createAsset(payload);
      showSnackbar('Asset created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await ledgerAPI.updateAsset(assetIdOf(editing), {
        ...(editing.asset_name?.trim() ? { asset_name: editing.asset_name.trim() } : {}),
        ...(editing.description != null ? { description: editing.description } : {}),
        ...(editing.custodian != null ? { custodian: editing.custodian } : {}),
      });
      showSnackbar('Asset updated', 'success');
      setEditing(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getAsset(assetIdOf(row));
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch asset'), 'error');
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this asset?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Asset ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const submitCapitalize = async () => {
    if (!capDlg.funding_account_id) {
      showSnackbar('Funding account is required', 'error');
      return;
    }
    try {
      await ledgerAPI.capitalizeAsset(assetIdOf(capDlg.item), {
        funding_account_id: Number(capDlg.funding_account_id),
        ...(capDlg.capitalization_date ? { capitalization_date: capDlg.capitalization_date } : {}),
        ...(capDlg.supplier_id ? { supplier_id: Number(capDlg.supplier_id) } : {}),
      });
      showSnackbar('Asset capitalised (ACTIVE)', 'success');
      setCapDlg({ open: false, item: null, funding_account_id: '', capitalization_date: '', supplier_id: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitTransfer = async () => {
    if (!transferDlg.department_id && !transferDlg.location_id && !transferDlg.cost_centre_id && !transferDlg.branch_id && !transferDlg.custodian?.trim()) {
      showSnackbar('Pick at least one destination (department, location, cost centre, branch or custodian)', 'error');
      return;
    }
    try {
      await ledgerAPI.transferAsset(assetIdOf(transferDlg.item), {
        ...(transferDlg.department_id ? { department_id: Number(transferDlg.department_id) } : {}),
        ...(transferDlg.location_id ? { location_id: Number(transferDlg.location_id) } : {}),
        ...(transferDlg.cost_centre_id ? { cost_centre_id: Number(transferDlg.cost_centre_id) } : {}),
        ...(transferDlg.branch_id ? { branch_id: Number(transferDlg.branch_id) } : {}),
        ...(transferDlg.custodian?.trim() ? { custodian: transferDlg.custodian.trim() } : {}),
        ...(transferDlg.reason?.trim() ? { reason: transferDlg.reason.trim() } : {}),
      });
      showSnackbar('Asset transferred', 'success');
      setTransferDlg({ open: false, item: null, department_id: '', location_id: '', cost_centre_id: '', branch_id: '', custodian: '', reason: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitDispose = async () => {
    if (!(Number(disposeDlg.proceeds || 0) >= 0) || (disposeDlg.proceeds === '' && disposeDlg.disposal_type === 'SALE')) {
      showSnackbar('Proceeds are required for a SALE (0 for scrap/retirement)', 'error');
      return;
    }
    if (Number(disposeDlg.proceeds || 0) > 0 && !disposeDlg.settlement_account_id) {
      showSnackbar('Settlement account is required when proceeds are greater than zero', 'error');
      return;
    }
    if (!disposeDlg.gain_loss_account_id) {
      showSnackbar('Gain/loss P&L account is required', 'error');
      return;
    }
    try {
      await ledgerAPI.disposeAsset(assetIdOf(disposeDlg.item), {
        disposal_type: disposeDlg.disposal_type,
        disposal_date: disposeDlg.disposal_date,
        proceeds: Number(disposeDlg.proceeds || 0),
        ...(disposeDlg.settlement_account_id ? { settlement_account_id: Number(disposeDlg.settlement_account_id) } : {}),
        gain_loss_account_id: Number(disposeDlg.gain_loss_account_id),
        ...(disposeDlg.notes?.trim() ? { notes: disposeDlg.notes.trim() } : {}),
      });
      showSnackbar('Asset disposed', 'success');
      setDisposeDlg({ open: false, item: null, disposal_type: 'SALE', disposal_date: '', proceeds: '', settlement_account_id: '', gain_loss_account_id: '', notes: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const netBook = (a) => {
    if (a?.net_book_value != null) return Number(a.net_book_value);
    if (a?.cost_amount != null) return Number(a.cost_amount) - Number(a?.accumulated_depreciation ?? 0);
    if (a?.acquisition_cost != null) return Number(a.acquisition_cost) - Number(a?.accumulated_depreciation ?? 0);
    return null;
  };
  const costOf = (a) => a?.cost_amount ?? a?.acquisition_cost;

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.ASSETS}
        title="Asset Register"
        subtitle="Every physical asset, its cost and net book value"
        addLabel="New Asset"
        idKey="asset_id"
        searchPlaceholder="Search asset #, name..."
        emptyMessage="No assets found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getAssets(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'category_id', label: 'Category', picker: AssetCategoryPicker },
        ]}
        columns={[
          { label: 'Asset #', render: (a) => <strong>{a.asset_number || `AST-${assetIdOf(a)}`}</strong> },
          { label: 'Name', render: (a) => a.asset_name },
          { label: 'Category', render: (a) => a.category?.category_name || (a.category_id ? `Category ${a.category_id}` : '-'), hideOnMobile: true },
          { label: 'Acquired', render: (a) => fmtDate(a.acquisition_date), hideOnMobile: true },
          { label: 'Cost', render: (a) => formatAccountingAmount(costOf(a)), align: 'right' },
          { label: 'NBV', render: (a) => formatAccountingAmount(netBook(a)), align: 'right' },
          { label: 'Status', render: (a) => <AssetStatusChip status={a.status} /> },
        ]}
        rowActions={(a) => {
          const id = assetIdOf(a);
          const status = String(a.status || 'DRAFT').toUpperCase();
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(a) }];
          if (status === 'DRAFT' && canUpdate) {
            actions.push({ icon: EditIcon, title: 'Edit', color: 'primary', onClick: () => setEditing({ ...a }) });
          }
          if (status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve asset', 'approved', () => ledgerAPI.approveAsset(id)) });
          }
          if (status === 'APPROVED' && canApprove) {
            actions.push({ icon: CapitalizeIcon, title: 'Capitalise', color: 'primary', onClick: () => setCapDlg({ open: true, item: a, funding_account_id: '', capitalization_date: new Date().toISOString().slice(0, 10), supplier_id: '' }) });
          }
          if (status === 'ACTIVE' && canUpdate) {
            actions.push({ icon: TransferIcon, title: 'Transfer custody', color: 'primary', onClick: () => setTransferDlg({ open: true, item: a, department_id: '', location_id: '', cost_centre_id: '', branch_id: '', custodian: '', reason: '' }) });
          }
          if (status === 'ACTIVE' && canApprove) {
            actions.push({ icon: DisposeIcon, title: 'Dispose', color: 'error', onClick: () => setDisposeDlg({ open: true, item: a, disposal_type: 'SALE', disposal_date: new Date().toISOString().slice(0, 10), proceeds: '', settlement_account_id: '', gain_loss_account_id: '', notes: '' }) });
          }
          if (status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete asset',
                message: `Delete DRAFT asset ${a.asset_number || `AST-${id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteAsset(id);
                    showSnackbar('Asset deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Asset</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Asset Name" value={values.asset_name}
                onChange={(e) => setValues((p) => ({ ...p, asset_name: e.target.value }))} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AssetCategoryPicker value={values.category_id} onChange={(id) => setValues((p) => ({ ...p, category_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField
                fullWidth size="small" type="date" label="Acquisition Date" value={values.acquisition_date}
                onChange={(e) => setValues((p) => ({ ...p, acquisition_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField
                fullWidth size="small" type="number" label="Cost Amount" value={values.cost_amount}
                onChange={(e) => setValues((p) => ({ ...p, cost_amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField
                fullWidth size="small" type="number" label="Salvage Value" value={values.salvage_value}
                onChange={(e) => setValues((p) => ({ ...p, salvage_value: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SupplierPicker value={values.supplier_id} onChange={(id) => setValues((p) => ({ ...p, supplier_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Custodian" value={values.custodian}
                onChange={(e) => setValues((p) => ({ ...p, custodian: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DepartmentPicker value={values.department_id} onChange={(id) => setValues((p) => ({ ...p, department_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocationPicker value={values.location_id} onChange={(id) => setValues((p) => ({ ...p, location_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CostCentrePicker value={values.cost_centre_id} onChange={(id) => setValues((p) => ({ ...p, cost_centre_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BranchPicker value={values.branch_id} onChange={(id) => setValues((p) => ({ ...p, branch_id: id }))} />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Description" value={values.description}
                onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Asset (DRAFT only)</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label="Asset Name" value={editing.asset_name ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, asset_name: e.target.value }))} required
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label="Custodian" value={editing.custodian ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, custodian: e.target.value }))}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" multiline rows={2} label="Description" value={editing.description ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Asset {detail?.asset_number || (detail ? `AST-${assetIdOf(detail)}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="h6">{detail.asset_name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detail.category?.category_name || (detail.category_id ? `Category ${detail.category_id}` : '')}
                {' '} - acquired {fmtDate(detail.acquisition_date)}
                {detail.custodian ? ` - custodian: ${detail.custodian}` : ''}
              </Typography>
              <DataTable
                columns={['Field', 'Value']}
                data={[
                  ['Cost', formatAccountingAmount(costOf(detail))],
                  ['Salvage value', formatAccountingAmount(detail.salvage_value)],
                  ['Accumulated depreciation', formatAccountingAmount(detail.accumulated_depreciation)],
                  ['Net book value', formatAccountingAmount(netBook(detail))],
                  ['Department', detail.department?.department_name || '-'],
                  ['Location', detail.location?.location_name || '-'],
                ]}
                loading={false}
                emptyMessage="No detail."
                renderRow={(row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row[0]}</TableCell>
                    <TableCell align="right">{row[1]}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                <AssetStatusChip status={detail.status} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={capDlg.open}
        onClose={() => setCapDlg({ open: false, item: null, funding_account_id: '', capitalization_date: '', supplier_id: '' })}
        title={`Capitalise asset ${capDlg.item?.asset_number || ''}`}
        onSubmit={submitCapitalize}
        values={capDlg}
        onChange={(key, value) => setCapDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'funding_account_id', label: 'Funding Account (bank, AP clearing, donor capital)', type: 'picker', picker: AccountPicker, required: true, md: 12 },
          { key: 'capitalization_date', label: 'Capitalisation Date', type: 'date', md: 6 },
          { key: 'supplier_id', label: 'Supplier', type: 'picker', picker: SupplierPicker, md: 6 },
        ]}
      />

      <LedgerFormDialog
        open={transferDlg.open}
        onClose={() => setTransferDlg({ open: false, item: null, department_id: '', location_id: '', cost_centre_id: '', branch_id: '', custodian: '', reason: '' })}
        title={`Transfer custody of ${transferDlg.item?.asset_number || ''}`}
        onSubmit={submitTransfer}
        values={transferDlg}
        onChange={(key, value) => setTransferDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'department_id', label: 'Department', type: 'picker', picker: DepartmentPicker, md: 6 },
          { key: 'location_id', label: 'Location', type: 'picker', picker: LocationPicker, md: 6 },
          { key: 'cost_centre_id', label: 'Cost Centre', type: 'picker', picker: CostCentrePicker, md: 6 },
          { key: 'branch_id', label: 'Branch', type: 'picker', picker: BranchPicker, md: 6 },
          { key: 'custodian', label: 'Custodian', md: 6 },
          { key: 'reason', label: 'Reason', type: 'textarea', md: 12 },
        ]}
      />

      <Dialog open={disposeDlg.open} onClose={() => setDisposeDlg({ open: false, item: null, disposal_type: 'SALE', disposal_date: '', proceeds: '', settlement_account_id: '', gain_loss_account_id: '', notes: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Dispose asset {disposeDlg.item?.asset_number || ''}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={6}>
              <TextField fullWidth size="small" select label="Disposal Type" value={disposeDlg.disposal_type} onChange={(e) => setDisposeDlg((p) => ({ ...p, disposal_type: e.target.value }))}>
                {DISPOSAL_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Disposal Date" value={disposeDlg.disposal_date}
                onChange={(e) => setDisposeDlg((p) => ({ ...p, disposal_date: e.target.value }))} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Proceeds (0 if scrapped)" value={disposeDlg.proceeds}
                onChange={(e) => setDisposeDlg((p) => ({ ...p, proceeds: e.target.value }))} inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={6}>
              <AccountPicker label="Settlement Account (required if proceeds > 0)" value={disposeDlg.settlement_account_id} onChange={(id) => setDisposeDlg((p) => ({ ...p, settlement_account_id: id }))} />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Gain/Loss P&L Account" value={disposeDlg.gain_loss_account_id} onChange={(id) => setDisposeDlg((p) => ({ ...p, gain_loss_account_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Notes" value={disposeDlg.notes} onChange={(e) => setDisposeDlg((p) => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposeDlg({ open: false, item: null, disposal_type: 'SALE', disposal_date: '', proceeds: '', settlement_account_id: '', gain_loss_account_id: '', notes: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitDispose}>Dispose</Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default AssetRegisterTab;
